import type {
  Argument,
  ArgumentType,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import isNil from 'lodash/isNil';

import { FieldValue } from '../components/WorkcellFormModal/types';

export enum FieldType {
  Checkbox = 'checkbox',
  Float = 'float',
  Multiselect = 'multiselect',
  Number = 'number',
  Text = 'text',
}

export const getFieldType = (type: ArgumentType | undefined): FieldType => {
  if (Array.isArray(type)) return FieldType.Multiselect;

  switch (type) {
    case 'bool':
      return FieldType.Checkbox;
    case 'float':
      return FieldType.Float;
    case 'int':
      return FieldType.Number;
    default:
      return FieldType.Text;
  }
};

export const getNumberOfDecimals = (n: number): number => {
  const [coefficient, exponentStr] = n.toString().split('e');
  const exponent = exponentStr ? Number(exponentStr) : 0;
  const coefficientDecimals = coefficient?.split('.')[1]?.length ?? 0;

  return Math.max(coefficientDecimals - exponent, 0);
};

export const validateNumberField = (
  value: FieldValue,
  config: Argument
): string | null => {
  if (value === '') {
    return config.is_required ? 'Field is required.' : null;
  }

  const parsedValue = +value;
  if (Number.isNaN(parsedValue)) {
    return 'Invalid value.';
  }

  if (config.type === 'int' && !Number.isInteger(parsedValue)) {
    return 'Number should be integer.';
  }

  if (parsedValue < -9999999) {
    return 'Value exceeds min input: -9,999,999.';
  }

  if (parsedValue > 9999999) {
    return 'Value exceeds max input: 9,999,999.';
  }

  const numberOfDecimals = getNumberOfDecimals(parsedValue);
  if (numberOfDecimals > 6) {
    return 'Value exceeds 6 decimal places.';
  }

  if (typeof config.min_value === 'number' && parsedValue < config.min_value) {
    return `Number cannot be less than ${config.min_value}.`;
  }

  if (typeof config.max_value === 'number' && parsedValue > config.max_value) {
    return `Number cannot be greater than ${config.max_value}.`;
  }

  return null;
};

export const validateDurationField = (
  value: FieldValue,
  config: Argument
): string | null => {
  const parsedValue = +value;
  if (value === '' || parsedValue === 0) {
    return config.is_required ? 'Field is required.' : null;
  }

  if (Number.isNaN(parsedValue)) {
    return 'Invalid value.';
  }

  return null;
};

export const validateMultiselectField = (
  value: FieldValue,
  config: Argument
): string | null => {
  if (!Array.isArray(value)) {
    return 'Invalid value.';
  }

  if (config.is_required && value.length === 0) {
    return 'Field is required.';
  }

  return null;
};

export const validateTextField = (
  value: FieldValue,
  config: Argument
): string | null => {
  if (config.is_required && !value) {
    return 'Field is required.';
  }

  return null;
};

export const validateField = (
  value: FieldValue,
  config: Argument
): string | null => {
  const type = getFieldType(config.type);

  if (isNil(value)) {
    return config.is_required ? 'Field is required.' : null;
  }

  if (type === FieldType.Number && config.units === 'seconds') {
    return validateDurationField(value, config);
  }

  switch (type) {
    case FieldType.Checkbox:
      return null;
    case FieldType.Float:
    case FieldType.Number:
      return validateNumberField(value, config);
    case FieldType.Multiselect:
      return validateMultiselectField(value, config);
    default:
      return validateTextField(value, config);
  }
};
